import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  acceptInviteToken,
  cancelInviteToken,
  deleteAffiliation,
  getUserPendingInvites,
} from "../../api/api";
import { defaultPfpUrl } from "../utils";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { cn } from "../../lib/utils";
import SignedOutPrompt from "../SignedOutPrompt";
import {
  NotificationEmptyState,
  NotificationErrorPanel,
} from "../notifications_page/NotificationTabShared";
import { useUserRealtime } from "../../context/UserRealtimeContext";

const TABS = ["all", "pending"];

/**
 * @param {number} [size]
 * @returns {JSX.Element}
 */
function ChevronIcon({ size = 14 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

/**
 * Vertical-dots glyph for the overflow trigger.
 * @param {{ className?: string }} [props]
 * @returns {JSX.Element}
 */
function IconMoreVertical({ className = "h-[18px] w-[18px]" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="5" r="1" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
      <circle cx="12" cy="19" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
}

/**
 * @param {string} name
 * @returns {string}
 */
function initials(name) {
  return (name || "OR")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 2)
    .toUpperCase() || "OR";
}

/**
 * 3-dot menu with a Leave action.
 * @param {Object} props
 * @param {() => void} props.onLeave
 * @returns {JSX.Element}
 */
function LeaveOverflowMenu({ onLeave }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) {
      return undefined;
    }
    /**
     * @param {MouseEvent|TouchEvent} event
     * @returns {void}
     */
    function handlePointerDown(event) {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [menuOpen]);

  return (
    <div className="relative shrink-0" ref={wrapRef}>
      <button
        type="button"
        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-text-muted hover:bg-secondary hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring"
        aria-label="More actions"
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen((open) => !open)}
      >
        <IconMoreVertical />
      </button>
      {menuOpen ? (
        <div
          className={cn(
            "absolute right-0 z-30 min-w-[9rem] overflow-hidden rounded-md border border-ui-border bg-ui-card text-sm shadow-md",
            "top-full mt-1"
          )}
        >
          <div className="py-1">
            <button
              type="button"
              className="flex w-full items-center px-3 py-2 text-left text-delete hover:bg-highlight"
              onClick={() => {
                setMenuOpen(false);
                onLeave();
              }}
            >
              Leave
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * One organization affiliation / invite row.
 * @param {Object} props
 * @param {string} props.name
 * @param {string} [props.href]
 * @param {string} [props.pfpUrl]
 * @param {React.ReactNode} [props.meta]
 * @param {React.ReactNode} props.actions
 * @returns {JSX.Element}
 */
function AffiliationRow({ name, href, pfpUrl, meta, actions }) {
  const avatar = (
    <Avatar className="h-16 w-16 shrink-0 border-2 border-ui-border">
      <AvatarImage
        src={pfpUrl || defaultPfpUrl}
        alt=""
        onError={(event) => {
          event.currentTarget.onerror = null;
          event.currentTarget.src = defaultPfpUrl;
        }}
      />
      <AvatarFallback className="text-sm text-text-secondary">{initials(name)}</AvatarFallback>
    </Avatar>
  );

  return (
    <Card className="border-ui-border shadow-profile-sm transition-colors hover:border-accent">
      <CardContent className="p-4 sm:p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
            {href ? (
              <Link to={href} className="shrink-0">
                {avatar}
              </Link>
            ) : (
              avatar
            )}
            <div className="min-w-0 space-y-1">
              {href ? (
                <Link
                  to={href}
                  className="text-lg font-semibold text-accent transition-colors hover:text-text-accent-hover"
                >
                  {name}
                </Link>
              ) : (
                <span className="text-lg font-semibold text-text-primary">{name}</span>
              )}
              {meta}
            </div>
          </div>
          <div className="flex shrink-0 items-center justify-end gap-2">{actions}</div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Manage affiliations: All (active) and Pending (invites) under Network.
 * @param {Object} props
 * @param {number} props.userId
 * @param {object|null} props.user
 * @param {function} [props.refreshUser]
 * @returns {JSX.Element}
 */
function ManageAffiliationsPage({ userId, user, refreshUser }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const tab = TABS.includes(requestedTab) ? requestedTab : "all";

  const [pendingInvites, setPendingInvites] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const { refreshPendingAffiliations } = useUserRealtime();

  const activeAffiliations =
    user?.affiliations?.filter((aff) => aff.status === "active") || [];

  useEffect(() => {
    let cancelled = false;
    const email = user?.email || "";
    if (!email) {
      setPendingInvites([]);
      return undefined;
    }
    (async () => {
      const next = await getUserPendingInvites(email);
      if (cancelled) return;
      if (next.error || !next.success) {
        setError(next.error || "Failed to load pending invites");
        setPendingInvites([]);
        return;
      }
      setError(null);
      setPendingInvites(next.data || []);
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.email]);

  /**
   * @param {string} nextTab
   * @returns {void}
   */
  const handleTabChange = (nextTab) => {
    if (nextTab === "all") {
      setSearchParams({}, { replace: true });
    } else {
      setSearchParams({ tab: nextTab }, { replace: true });
    }
  };

  /**
   * @returns {Promise<void>}
   */
  const handleConfirm = async () => {
    if (!confirm || busy) return;
    setBusy(true);
    setError(null);
    try {
      if (confirm.type === "leave") {
        const orgId = confirm.affiliation.org_id || confirm.affiliation.organization?.id;
        const result = await deleteAffiliation(userId, orgId);
        if (result.success) {
          setConfirm(null);
          if (refreshUser) await refreshUser();
        } else {
          setError(result.error || "Failed to leave organization");
        }
      } else if (confirm.type === "decline") {
        const result = await cancelInviteToken(confirm.invite.token);
        if (result.success) {
          setPendingInvites((prev) =>
            prev.filter((invite) => invite.token !== confirm.invite.token)
          );
          setConfirm(null);
          await refreshPendingAffiliations();
        } else {
          setError(result.error || "Failed to decline invite");
        }
      }
    } catch (err) {
      setError("Network error while updating affiliation");
    } finally {
      setBusy(false);
    }
  };

  /**
   * @param {object} invite
   * @returns {Promise<void>}
   */
  const handleAccept = async (invite) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await acceptInviteToken(invite.token);
      if (result.success) {
        setPendingInvites((prev) => prev.filter((item) => item.token !== invite.token));
        await refreshPendingAffiliations();
        if (refreshUser) await refreshUser();
      } else {
        setError(result.error || "Failed to accept invite");
      }
    } catch (err) {
      setError("Network error while accepting invite");
    } finally {
      setBusy(false);
    }
  };

  if (!userId) {
    return (
      <SignedOutPrompt
        title="Affiliations"
        description="Sign in to view your organization affiliations and respond to invitations."
      />
    );
  }

  const confirmCopy = {
    leave: {
      title: "Leave organization?",
      description: `Are you sure you want to leave ${confirm?.name || "this organization"}?`,
      action: "Leave",
    },
    decline: {
      title: "Decline invitation?",
      description: `Are you sure you want to decline this invitation from ${confirm?.name || "this organization"}?`,
      action: "Decline",
    },
  }[confirm?.type] || { title: "", description: "", action: "" };

  return (
    <div className="mx-auto max-w-4xl space-y-6 px-4 py-6 pb-12">
      <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-2 text-sm">
        <Link
          to="/network"
          className="font-medium text-accent underline underline-offset-2 hover:text-text-accent-hover"
        >
          Network
        </Link>
        <span className="text-text-muted" aria-hidden>
          <ChevronIcon />
        </span>
        <span className="text-text-secondary" aria-current="page">
          Affiliations
        </span>
      </nav>

      <div>
        <h1 className="font-display text-2xl font-medium text-text-primary sm:text-3xl">
          Affiliations
        </h1>
        <p className="mt-1 text-sm text-text-muted">
          Review organizations you belong to and respond to pending invitations.
        </p>
      </div>

      {error ? <NotificationErrorPanel message={error} /> : null}

      <Tabs value={tab} onValueChange={handleTabChange}>
        <TabsList className="mb-4 grid w-full grid-cols-2">
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="pending" className="gap-2">
            Pending
            {pendingInvites.length > 0 ? (
              <Badge variant="secondary" className="h-5 min-w-5 px-1.5">
                {pendingInvites.length > 9 ? "9+" : pendingInvites.length}
              </Badge>
            ) : null}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all">
          {activeAffiliations.length === 0 ? (
            <NotificationEmptyState
              title="No affiliations yet"
              subtitle="Organizations you join will appear here."
            />
          ) : (
            <ul className="list-none space-y-4 p-0">
              {activeAffiliations.map((affiliation) => {
                const org = affiliation.organization || {};
                const orgId = affiliation.org_id || org.id;
                const name = org.name || "Unknown organization";
                return (
                  <li key={affiliation.id}>
                    <AffiliationRow
                      name={name}
                      href={orgId ? `/organization/${orgId}` : undefined}
                      pfpUrl={org.pfp_url}
                      meta={
                        <>
                          {affiliation.role ? (
                            <p className="text-sm text-text-secondary">
                              Role:{" "}
                              <span className="font-medium text-text-primary">
                                {affiliation.role}
                              </span>
                            </p>
                          ) : null}
                          {affiliation.created_at ? (
                            <p className="text-sm text-text-muted">
                              Joined {new Date(affiliation.created_at).toLocaleDateString()}
                            </p>
                          ) : null}
                        </>
                      }
                      actions={
                        <LeaveOverflowMenu
                          onLeave={() =>
                            setConfirm({ type: "leave", affiliation, name })
                          }
                        />
                      }
                    />
                  </li>
                );
              })}
            </ul>
          )}
        </TabsContent>

        <TabsContent value="pending">
          {pendingInvites.length === 0 ? (
            <NotificationEmptyState
              title="No pending affiliation requests"
              subtitle="You are all caught up."
            />
          ) : (
            <ul className="list-none space-y-4 p-0">
              {pendingInvites.map((invite) => {
                const name = invite.organization_name || "Unknown organization";
                return (
                  <li key={invite.token}>
                    <AffiliationRow
                      name={name}
                      href={invite.org_id ? `/organization/${invite.org_id}` : undefined}
                      meta={
                        <>
                          <Badge variant="secondary" className="font-normal capitalize">
                            pending invite
                          </Badge>
                          {invite.role ? (
                            <p className="text-sm text-text-secondary">
                              Role:{" "}
                              <span className="font-medium text-text-primary">{invite.role}</span>
                            </p>
                          ) : null}
                          {invite.created_at ? (
                            <p className="text-sm text-text-muted">
                              Requested {new Date(invite.created_at).toLocaleDateString()}
                            </p>
                          ) : null}
                          {invite.expires_at ? (
                            <p className="text-sm text-text-muted">
                              Expires {new Date(invite.expires_at).toLocaleDateString()}
                            </p>
                          ) : null}
                        </>
                      }
                      actions={
                        <>
                          <Button
                            type="button"
                            disabled={busy}
                            onClick={() => handleAccept(invite)}
                          >
                            Accept
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            disabled={busy}
                            onClick={() => setConfirm({ type: "decline", invite, name })}
                          >
                            Decline
                          </Button>
                        </>
                      }
                    />
                  </li>
                );
              })}
            </ul>
          )}
        </TabsContent>
      </Tabs>

      <Dialog
        open={Boolean(confirm)}
        onOpenChange={(open) => {
          if (!open && !busy) setConfirm(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{confirmCopy.title}</DialogTitle>
            <DialogDescription>{confirmCopy.description}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={() => setConfirm(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={busy}
              onClick={handleConfirm}
            >
              {busy ? "Loading…" : confirmCopy.action}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ManageAffiliationsPage;
